import { useState } from "react";
import { GRADES, BRANDS, FAQ } from "../lib/data";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Plus, Truck, Ruler, Scale, ShieldCheck, Handshake, Trophy, Network, Hammer, TrendingUp, Award } from "lucide-react";
import Reveal from "../components/Reveal";

const SERVICES = [
  { icon: Truck, title: "Same-day dispatch", text: "In-house fleet across Hyderabad & Secunderabad." },
  { icon: Scale, title: "Calibrated weighbridges", text: "Fair weight — you pay only for what you lift." },
  { icon: Ruler, title: "Customised rods according to site requirement", text: "Standard 12 m rods bent to 6 m, cut-to-size, and tailored as per your site's exact requirement." },
  { icon: ShieldCheck, title: "BIS-certified stock", text: "Every brand we stock meets IS 1786 standards." },
];

const PURCHASE_BENEFITS = [
  "Quantity estimation assistance",
  "Brand selection guidance",
  "Timely dispatch coordination",
  "Multi-branch supply support",
  "Transparent live pricing",
  "Hassle-free procurement experience",
];

const WHY_US = [
  {
    icon: Trophy,
    title: "50+ Years of Trust",
    text: "Serving Hyderabad and Secunderabad since 1969 with long-standing relationships built on reliability and consistency.",
  },
  {
    icon: Scale,
    title: "Fair Weight, Every Time",
    text: "Every dispatch is handled with transparency and precision, ensuring customers receive accurate weights and dependable material quality.",
  },
  {
    icon: Network,
    title: "Multi-Branch Network",
    text: "Strategically located branches across Hyderabad enable faster supply coordination and smoother logistics.",
  },
  {
    icon: Hammer,
    title: "Trusted by Builders",
    text: "Supplying steel to leading builders, infrastructure companies, educational institutions, and industrial projects across the region.",
  },
  {
    icon: TrendingUp,
    title: "Live Market Pricing",
    text: "Dynamic pricing updates help customers stay informed and make better purchasing decisions with transparency.",
  },
  {
    icon: Award,
    title: "Premium Brand Associations",
    text: "Authorized supply and distribution of trusted TMT brands known for strength, consistency, and reliability.",
  },
];

export default function Products() {
  const [activeGrade, setActiveGrade] = useState(GRADES[1].code);
  const current = GRADES.find((g) => g.code === activeGrade);

  return (
    <div data-testid="products-page" className="grain bg-[#f7f7f5]">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-black/5 bg-gradient-to-b from-[#f7f7f5] to-white py-24">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute -right-32 top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full bg-[#fff1e8] opacity-40 blur-3xl" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Products &amp; Services
            </div>
            <h1 className="mt-5 font-display text-5xl uppercase leading-[0.9] tracking-tighter text-[#1c1c1e] md:text-7xl lg:text-[88px]">
              Engineered<br />
              <span className="text-[#e85a1c]">for load.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#4a4a4e]">
              A full-spectrum TMT inventory — from Fe 415 for low-rise residential
              to Fe 600 for seismic-grade infrastructure — backed by the services
              you actually need at the site.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Grades */}
      <section className="border-b border-black/5 bg-white py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Grades of TMT
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-6xl">
              Pick the grade<br />your project needs.
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
            {GRADES.map((g) => (
              <button
                key={g.code}
                data-testid={`grade-tab-${g.code}`}
                onClick={() => setActiveGrade(g.code)}
                className={`rounded-2xl border p-5 text-left transition-all duration-300 ${
                  activeGrade === g.code
                    ? "border-[#e85a1c] bg-[#e85a1c] text-white shadow-orange"
                    : "border-black/5 bg-white text-[#1c1c1e] shadow-lux hover:-translate-y-0.5 hover:border-[#e85a1c]/30"
                }`}
              >
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] opacity-70">
                  Grade
                </div>
                <div className="mt-2 font-display text-3xl tracking-tighter">
                  {g.code}
                </div>
              </button>
            ))}
          </div>

          <Reveal
            data-testid="grade-detail"
            className="mt-6"
          >
            <div
              key={current.code}
              data-testid={`grade-detail-${current.code.replace(/\s+/g, "")}`}
              className="grid animate-grade-pop grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6"
            >
              {[
                { label: "Yield Strength", value: current.yieldStrength, accent: true, testid: "spec-yield" },
                { label: "Min. Elongation", value: current.elongation, testid: "spec-elongation" },
                { label: "Carbon (C)", value: current.carbon, testid: "spec-carbon" },
                { label: "Phosphorus (P)", value: current.phosphorus, testid: "spec-phosphorus" },
                { label: "Sulphur (S)", value: current.sulphur, testid: "spec-sulphur" },
                { label: "Best For", value: current.use, isText: true, testid: "spec-bestfor" },
              ].map((c) => (
                <div
                  key={c.label}
                  data-testid={c.testid}
                  className={`group flex h-full flex-col rounded-2xl border border-black/5 bg-white p-5 shadow-lux transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#e85a1c]/25 hover:shadow-lux-hover hover:ring-2 hover:ring-[#e85a1c]/10 md:p-6 ${
                    c.isText ? "col-span-2 md:col-span-3 lg:col-span-1" : ""
                  }`}
                >
                  <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                    {c.label}
                  </div>
                  {c.isText ? (
                    <p className="mt-3 text-[15px] leading-[1.55] text-[#1c1c1e]">
                      {c.value}
                    </p>
                  ) : (
                    <div
                      className={`mt-3 font-display tracking-tighter ${
                        c.accent ? "text-[#e85a1c]" : "text-[#1c1c1e]"
                      } text-[26px] md:text-[30px] lg:text-[28px] xl:text-[32px]`}
                    >
                      {c.value}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Services */}
      <section className="border-b border-black/5 bg-[#f7f7f5] py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Services
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-6xl">
              Beyond the rod.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal
                key={s.title}
                delay={i * 80}
                data-testid={`service-${i}`}
                className="group rounded-2xl border border-black/5 bg-white p-7 shadow-lux transition-all duration-300 hover:-translate-y-1 hover:shadow-lux-hover"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#fff1e8] text-[#e85a1c] transition-colors group-hover:bg-[#e85a1c] group-hover:text-white">
                  <s.icon size={20} />
                </span>
                <div className="mt-6 font-display-med text-xl uppercase tracking-tight text-[#1c1c1e]">
                  {s.title}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#4a4a4e]">{s.text}</p>
              </Reveal>
            ))}
          </div>

          {/* Purchase as a Service — flagship offering */}
          <Reveal
            delay={120}
            data-testid="purchase-as-a-service"
            className="mt-8 overflow-hidden rounded-3xl border border-[#e85a1c]/20 bg-gradient-to-br from-white via-[#fff8f3] to-[#fff1e8] shadow-lux"
          >
            <div className="grid gap-0 md:grid-cols-12">
              <div className="md:col-span-5 p-8 md:p-10">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e85a1c] text-white shadow-orange">
                  <Handshake size={24} />
                </span>
                <div className="mt-6 font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                  / Flagship offering
                </div>
                <h3 className="mt-3 font-display text-3xl uppercase leading-[0.95] tracking-tighter text-[#1c1c1e] md:text-4xl">
                  Purchase as<br />a Service
                </h3>
                <p className="mt-5 text-sm leading-relaxed text-[#4a4a4e] md:text-base">
                  We help builders, contractors, developers, and homeowners simplify
                  steel procurement through expert assistance, transparent pricing,
                  quantity guidance, and dependable supply coordination — ensuring
                  the right steel reaches your site at the right time.
                </p>
              </div>
              <div className="md:col-span-7 border-t border-[#e85a1c]/10 p-8 md:border-l md:border-t-0 md:p-10">
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#8b8b8f]">
                  What you get
                </div>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {PURCHASE_BENEFITS.map((b, i) => (
                    <li
                      key={b}
                      data-testid={`purchase-benefit-${i}`}
                      className="flex items-start gap-3 rounded-xl border border-black/5 bg-white/70 p-3.5 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-[#e85a1c]/30 hover:bg-white"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#e85a1c] text-[10px] font-bold text-white">
                        {i + 1}
                      </span>
                      <span className="text-sm leading-snug text-[#1c1c1e]">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why Baheti Steel Traders */}
      <section
        data-testid="why-us-section"
        className="border-b border-black/5 bg-white py-20"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Why us
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-6xl">
              Why Baheti<br />Steel Traders?
            </h2>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((w, i) => (
              <Reveal
                key={w.title}
                delay={i * 80}
                data-testid={`why-us-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-black/5 bg-[#f7f7f5] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#e85a1c]/30 hover:bg-white hover:shadow-lux-hover"
              >
                <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#fff1e8] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="relative">
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white text-[#e85a1c] shadow-lux transition-colors group-hover:bg-[#e85a1c] group-hover:text-white">
                      <w.icon size={20} />
                    </span>
                    <span className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#bababd]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="mt-6 font-display-med text-xl uppercase leading-tight tracking-tight text-[#1c1c1e]">
                    {w.title}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-[#4a4a4e]">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="border-b border-black/5 bg-white py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / Brands we deal in
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-6xl">
              Six brands,<br />zero compromise.
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {BRANDS.map((b, i) => (
              <Reveal
                key={b.name}
                delay={i * 70}
                data-testid={`product-brand-${b.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="group rounded-2xl border border-black/5 bg-[#f7f7f5] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#e85a1c]/40 hover:bg-white hover:shadow-lux-hover"
              >
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#8b8b8f] group-hover:text-[#e85a1c]">
                  {b.grade}
                </div>
                <div className="mt-3 font-display text-xl uppercase tracking-tight text-[#1c1c1e]">
                  {b.name}
                </div>
                <div className="mt-5 h-px w-full bg-black/5 group-hover:bg-[#e85a1c]/30" />
                <div className="mt-3 flex items-end justify-between">
                  <div className="text-xs text-[#8b8b8f]">{b.sizes || "8 – 32 mm"}</div>
                  <Plus size={16} className="text-[#bababd] transition-all group-hover:rotate-90 group-hover:text-[#e85a1c]" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-black/5 bg-[#f7f7f5] py-20">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:grid-cols-12 lg:px-10">
          <Reveal className="md:col-span-4">
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / FAQ
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-5xl">
              Questions<br />we hear a lot.
            </h2>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-[#4a4a4e]">
              Still not sure? Call us on +91 95335 61060 — we love talking tonnage.
            </p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-8" data-testid="faq-accordion">
            <div className="rounded-2xl border border-black/5 bg-white p-2 shadow-lux">
              <Accordion type="single" collapsible className="w-full">
                {FAQ.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`item-${i}`}
                    className="border-b border-black/5 last:border-b-0"
                  >
                    <AccordionTrigger
                      data-testid={`faq-q-${i}`}
                      className="px-5 py-5 text-left font-display-med text-base uppercase tracking-tight text-[#1c1c1e] hover:text-[#e85a1c] hover:no-underline md:px-6 md:text-lg"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5 pr-8 text-sm leading-relaxed text-[#4a4a4e] md:px-6 md:text-base">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
